import { useEffect, useState } from "react";
import { toast } from "sonner";
import { HardHat, Save, Loader2, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { getIloOptions, saveIloClassification, clearIloClassification } from "@/lib/api";

const emptyPleural = () => ({
  present: false,
  chest_wall_profile: [],
  chest_wall_face_on: [],
  diaphragm: [],
  other: [],
  calcified: [],
  width: {},
  extent: {},
});

const emptyIlo = () => ({
  quality: "",
  quality_comment: "",
  small_opacities_present: false,
  profusion: "",
  shape_primary: "",
  shape_secondary: "",
  zones: [],
  large_opacities: "0",
  plaques: emptyPleural(),
  cpa_obliteration: [],
  diffuse_thickening: emptyPleural(),
  symbols: [],
  comments: "",
});

const toggle = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);

const inputCls =
  "rounded-lg border border-zinc-800 bg-zinc-950 px-3 py-2 text-sm text-zinc-100 focus:border-teal-500 focus:ring-1 focus:ring-teal-500 outline-none";
const labelCls = "text-[10px] uppercase tracking-[0.14em] text-zinc-500 font-semibold block mb-1.5";

export default function ILOPanel({ exam, onExamUpdate }) {
  const [opts, setOpts] = useState(null);
  const [form, setForm] = useState(exam?.ilo_classification || emptyIlo());
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  const [result, setResult] = useState({
    summary: exam?.ilo_summary,
    interpretation: exam?.ilo_interpretation,
  });

  useEffect(() => {
    getIloOptions().then(setOpts).catch(() => toast.error("Gagal memuat opsi ILO"));
  }, []);

  useEffect(() => {
    setForm(exam?.ilo_classification || emptyIlo());
    setResult({ summary: exam?.ilo_summary, interpretation: exam?.ilo_interpretation });
  }, [exam?.id, exam?.ilo_updated_at]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const doSave = async () => {
    setSaving(true);
    try {
      const r = await saveIloClassification(exam.id, form);
      setResult({ summary: r.ilo_summary, interpretation: r.ilo_interpretation });
      toast.success("Klasifikasi ILO tersimpan");
      onExamUpdate?.();
    } catch (e) {
      toast.error("Gagal menyimpan ILO", {
        description: String(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || e.message).slice(0, 200),
      });
    } finally {
      setSaving(false);
    }
  };

  const doClear = async () => {
    if (!window.confirm("Hapus klasifikasi ILO dan kembalikan ke standar umum?")) return;
    try {
      await clearIloClassification(exam.id);
      toast.success("Klasifikasi ILO dihapus");
      onExamUpdate?.();
    } catch {
      toast.error("Gagal menghapus");
    }
  };

  if (!opts) {
    return (
      <div className="rounded-xl border border-zinc-900 bg-zinc-900/40 p-5 text-sm text-zinc-500 flex items-center gap-2">
        <Loader2 className="w-4 h-4 animate-spin" /> Memuat form ILO…
      </div>
    );
  }

  return (
    <div className="rounded-xl border border-teal-500/20 bg-teal-500/[0.03] overflow-hidden" data-testid="ilo-panel">
      <div className="px-5 py-4 border-b border-teal-500/10 flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <HardHat className="w-4 h-4 text-teal-400" />
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-teal-500/80 font-semibold">
              Klasifikasi ILO — Pneumokoniosis
            </div>
            <div className="text-xs text-zinc-400 mt-0.5">
              {result.summary ? (
                <span className="font-mono text-teal-300" data-testid="ilo-summary">{result.summary}</span>
              ) : (
                "ILO International Classification of Radiographs of Pneumoconioses (2011/2022)"
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={doClear}
            data-testid="ilo-clear-btn"
            className="inline-flex items-center gap-1.5 rounded-lg border border-zinc-800 bg-zinc-950 hover:border-rose-500/40 hover:text-rose-400 text-zinc-400 px-3 py-1.5 text-xs font-medium"
          >
            <Trash2 className="w-3.5 h-3.5" /> Hapus ILO
          </button>
          <button
            type="button"
            onClick={() => setOpen((v) => !v)}
            data-testid="ilo-toggle-btn"
            className="p-1.5 rounded-lg text-zinc-400 hover:text-zinc-100 hover:bg-zinc-900"
          >
            {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>

      {result.interpretation && (
        <div className="px-5 py-3 bg-teal-500/5 border-b border-teal-500/10 text-sm text-teal-100" data-testid="ilo-interpretation">
          {result.interpretation}
        </div>
      )}

      {open && (
        <div className="p-5 space-y-6">
          {/* 1. Quality */}
          <Section title="1. Kualitas Teknis Film">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className={labelCls}>Grade</label>
                <select
                  data-testid="ilo-quality-select"
                  value={form.quality}
                  onChange={(e) => set({ quality: e.target.value })}
                  className={`${inputCls} w-full`}
                >
                  <option value="">— pilih —</option>
                  {Object.entries(opts.quality).map(([k, v]) => (
                    <option key={k} value={k}>{k} — {v}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className={labelCls}>Komentar kualitas (opsional)</label>
                <input
                  data-testid="ilo-quality-comment"
                  value={form.quality_comment}
                  onChange={(e) => set({ quality_comment: e.target.value })}
                  placeholder="mis. underexposed, rotasi ringan"
                  className={`${inputCls} w-full`}
                />
              </div>
            </div>
          </Section>

          {/* 2. Small opacities */}
          <Section title="2. Opasitas Kecil (Small Opacities)">
            <label className="flex items-center gap-2 text-sm text-zinc-200 mb-3">
              <input
                type="checkbox"
                data-testid="ilo-small-present"
                checked={form.small_opacities_present}
                onChange={(e) => set({ small_opacities_present: e.target.checked, profusion: e.target.checked ? form.profusion : "" })}
                className="accent-teal-500"
              />
              Terdapat opasitas kecil
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className={labelCls}>Profusi (skala 12 poin)</label>
                <div className="grid grid-cols-4 gap-1" data-testid="ilo-profusion-grid">
                  {opts.profusion.map((p) => {
                    const cat = Number(p[0]);
                    const active = form.profusion === p;
                    return (
                      <button
                        key={p}
                        type="button"
                        data-testid={`ilo-profusion-${p.replace("/", "-")}`}
                        onClick={() => set({ profusion: p, small_opacities_present: cat > 0 || form.small_opacities_present })}
                        className={`rounded-md px-2 py-1.5 text-xs font-mono border transition-colors ${
                          active
                            ? "bg-teal-600 border-teal-500 text-white"
                            : cat >= 1
                            ? "bg-zinc-950 border-zinc-800 text-amber-300 hover:border-amber-500/40"
                            : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600"
                        }`}
                      >
                        {p}
                      </button>
                    );
                  })}
                </div>
                <div className="text-[10px] text-zinc-500 mt-1.5">Cut-off pneumokoniosis: ≥ 1/0</div>
              </div>
              <div className="space-y-3">
                <div>
                  <label className={labelCls}>Bentuk/ukuran primer</label>
                  <select
                    data-testid="ilo-shape-primary"
                    value={form.shape_primary}
                    onChange={(e) => set({ shape_primary: e.target.value })}
                    className={`${inputCls} w-full`}
                  >
                    <option value="">—</option>
                    {Object.entries(opts.shapes).map(([k, v]) => (
                      <option key={k} value={k}>{k} — {v}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>Bentuk/ukuran sekunder</label>
                  <select
                    data-testid="ilo-shape-secondary"
                    value={form.shape_secondary}
                    onChange={(e) => set({ shape_secondary: e.target.value })}
                    className={`${inputCls} w-full`}
                  >
                    <option value="">—</option>
                    {Object.entries(opts.shapes).map(([k, v]) => (
                      <option key={k} value={k}>{k} — {v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className={labelCls}>Zona paru terkena</label>
                <ZoneGrid zones={form.zones} onToggle={(z) => set({ zones: toggle(form.zones, z) })} labels={opts.zones} />
              </div>
            </div>
          </Section>

          {/* 3. Large opacities */}
          <Section title="3. Opasitas Besar (Large Opacities)">
            <div className="flex flex-wrap gap-2" data-testid="ilo-large-group">
              {Object.entries(opts.large).map(([k, v]) => (
                <button
                  key={k}
                  type="button"
                  data-testid={`ilo-large-${k}`}
                  onClick={() => set({ large_opacities: k })}
                  title={v}
                  className={`rounded-lg px-3 py-2 text-xs border text-left max-w-xs transition-colors ${
                    form.large_opacities === k
                      ? "bg-teal-600 border-teal-500 text-white"
                      : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600"
                  }`}
                >
                  {v}
                </button>
              ))}
            </div>
          </Section>

          {/* 4. Pleural */}
          <Section title="4. Abnormalitas Pleura">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
              <PleuralForm
                title="Plak pleura"
                testId="plaques"
                value={form.plaques}
                onChange={(v) => set({ plaques: v })}
                opts={opts}
              />
              <PleuralForm
                title="Penebalan pleura difus"
                testId="diffuse"
                value={form.diffuse_thickening}
                onChange={(v) => set({ diffuse_thickening: v })}
                opts={opts}
              />
            </div>
            <div className="mt-4">
              <label className={labelCls}>Obliterasi sudut kostofrenikus</label>
              <SideToggle value={form.cpa_obliteration} onChange={(v) => set({ cpa_obliteration: v })} testId="ilo-cpa" />
            </div>
          </Section>

          {/* 5. Symbols */}
          <Section title="5. Simbol (Symbols)">
            <div className="flex flex-wrap gap-1.5" data-testid="ilo-symbols">
              {Object.entries(opts.symbols).map(([k, v]) => {
                const active = form.symbols.includes(k);
                return (
                  <button
                    key={k}
                    type="button"
                    data-testid={`ilo-symbol-${k}`}
                    title={v}
                    onClick={() => set({ symbols: toggle(form.symbols, k) })}
                    className={`rounded-md px-2 py-1 text-[11px] font-mono border transition-colors ${
                      active
                        ? "bg-teal-600 border-teal-500 text-white"
                        : "bg-zinc-950 border-zinc-800 text-zinc-400 hover:border-zinc-600 hover:text-zinc-100"
                    }`}
                  >
                    {k}
                  </button>
                );
              })}
            </div>
            {form.symbols.length > 0 && (
              <div className="text-xs text-zinc-400 mt-2">
                {form.symbols.map((s) => `${s} = ${opts.symbols[s]}`).join(" · ")}
              </div>
            )}
          </Section>

          <div>
            <label className={labelCls}>Komentar</label>
            <textarea
              data-testid="ilo-comments"
              rows={2}
              value={form.comments}
              onChange={(e) => set({ comments: e.target.value })}
              className={`${inputCls} w-full resize-none`}
              placeholder="Catatan tambahan pembaca ILO (opsional)"
            />
          </div>

          <div className="flex items-center gap-2 pt-2 border-t border-zinc-900">
            <button
              type="button"
              onClick={doSave}
              disabled={saving}
              data-testid="ilo-save-btn"
              className="inline-flex items-center gap-1.5 rounded-lg bg-teal-600 hover:bg-teal-500 text-white px-4 py-2 text-sm font-medium shadow-lg shadow-teal-500/20 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Simpan Klasifikasi ILO
            </button>
            <span className="text-xs text-zinc-500">
              Hasil otomatis masuk ke draft laporan &amp; PDF.
            </span>
          </div>
        </div>
      )}
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="text-xs font-semibold text-zinc-200 mb-3 pb-1.5 border-b border-zinc-900">{title}</div>
      {children}
    </div>
  );
}

function ZoneGrid({ zones, onToggle, labels }) {
  const order = ["RU", "LU", "RM", "LM", "RL", "LL"];
  return (
    <div className="grid grid-cols-2 gap-1 max-w-[220px]" data-testid="ilo-zone-grid">
      {order.map((z) => (
        <button
          key={z}
          type="button"
          data-testid={`ilo-zone-${z}`}
          onClick={() => onToggle(z)}
          className={`rounded-md px-2 py-2 text-xs border transition-colors ${
            zones.includes(z)
              ? "bg-teal-600 border-teal-500 text-white"
              : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600"
          }`}
        >
          <span className="font-mono">{z}</span>
          <span className="block text-[9px] opacity-70">{labels[z]}</span>
        </button>
      ))}
    </div>
  );
}

function SideToggle({ value, onChange, testId }) {
  return (
    <div className="flex gap-1.5">
      {["R", "L"].map((s) => (
        <button
          key={s}
          type="button"
          data-testid={`${testId}-${s}`}
          onClick={() => onChange(toggle(value, s))}
          className={`rounded-md px-3 py-1.5 text-xs border font-mono transition-colors ${
            value.includes(s)
              ? "bg-teal-600 border-teal-500 text-white"
              : "bg-zinc-950 border-zinc-800 text-zinc-300 hover:border-zinc-600"
          }`}
        >
          {s === "R" ? "Kanan (R)" : "Kiri (L)"}
        </button>
      ))}
    </div>
  );
}

function PleuralForm({ title, testId, value, onChange, opts }) {
  const v = value || emptyPleural();
  const upd = (patch) => onChange({ ...v, ...patch });
  const sides = [
    ["chest_wall_profile", "Dinding dada in-profile"],
    ["chest_wall_face_on", "Dinding dada face-on"],
    ["diaphragm", "Diafragma"],
    ["other", "Lokasi lain"],
    ["calcified", "Kalsifikasi"],
  ];
  return (
    <div className="rounded-lg border border-zinc-900 bg-zinc-950/40 p-4" data-testid={`ilo-${testId}`}>
      <label className="flex items-center gap-2 text-sm text-zinc-200 mb-3">
        <input
          type="checkbox"
          data-testid={`ilo-${testId}-present`}
          checked={v.present}
          onChange={(e) => upd({ present: e.target.checked })}
          className="accent-teal-500"
        />
        {title}
      </label>
      {v.present && (
        <div className="space-y-2.5">
          {sides.map(([key, label]) => (
            <div key={key} className="flex items-center justify-between gap-2">
              <span className="text-xs text-zinc-400">{label}</span>
              <SideToggle value={v[key] || []} onChange={(nv) => upd({ [key]: nv })} testId={`ilo-${testId}-${key}`} />
            </div>
          ))}
          <div className="grid grid-cols-2 gap-2 pt-2 border-t border-zinc-900">
            {["R", "L"].map((s) => (
              <div key={s} className="space-y-1.5">
                <div className="text-[10px] uppercase tracking-wider text-zinc-500">{s === "R" ? "Kanan" : "Kiri"}</div>
                <select
                  data-testid={`ilo-${testId}-width-${s}`}
                  value={v.width?.[s] || ""}
                  onChange={(e) => upd({ width: { ...(v.width || {}), [s]: e.target.value } })}
                  className={`${inputCls} w-full text-xs py-1.5`}
                >
                  <option value="">Lebar —</option>
                  {Object.entries(opts.pleural_width).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
                <select
                  data-testid={`ilo-${testId}-extent-${s}`}
                  value={v.extent?.[s] || ""}
                  onChange={(e) => upd({ extent: { ...(v.extent || {}), [s]: e.target.value } })}
                  className={`${inputCls} w-full text-xs py-1.5`}
                >
                  <option value="">Luas —</option>
                  {Object.entries(opts.pleural_extent).map(([k, l]) => (
                    <option key={k} value={k}>{l}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
